import { Zap, AlertTriangle } from 'lucide-react';

interface UsageItem {
  featureId: string;
  featureName: string;
  usage: number;
  limit: number;
}

interface BillingStatusBadgeProps {
  planName: string;
  planId: string;
  usage: UsageItem[];
  onUpgradeClick: () => void;
  trialDaysRemaining?: number | null;
  trialExpired?: boolean;
}

function isUnlimited(limit: number): boolean {
  return limit >= 99999;
}

function getCounterColor(usage: number, limit: number): string {
  if (limit <= 0 || isUnlimited(limit)) return 'inherit';
  const pct = (usage / limit) * 100;
  if (pct >= 100) return '#ef4444'; // red-500
  if (pct >= 80) return '#f59e0b'; // amber-500
  return 'inherit';
}

export function BillingStatusBadge({ planName, planId, usage, onUpgradeClick, trialDaysRemaining, trialExpired }: BillingStatusBadgeProps) {
  const sources = usage.find(u => u.featureId === 'sources_connected');
  const posts = usage.find(u => u.featureId === 'posts_generated');

  const isFreePlan = planId === 'free';
  const isOverLimit = usage.some(u => u.limit > 0 && !isUnlimited(u.limit) && u.usage >= u.limit);
  const isNearLimit = usage.some(u => u.limit > 0 && !isUnlimited(u.limit) && (u.usage / u.limit) >= 0.8);

  // Trial expired takes priority for styling
  if (trialExpired) {
    return (
      <button
        onClick={onUpgradeClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.25rem 0.625rem',
          borderRadius: '9999px',
          border: '1px solid #fecaca',
          backgroundColor: '#fef2f2',
          cursor: 'pointer',
          fontSize: '0.75rem',
          lineHeight: '1rem',
          fontFamily: 'inherit',
          color: '#991b1b',
          transition: 'all 150ms ease',
        }}
        title="Your free trial has ended - click to upgrade"
      >
        <AlertTriangle style={{ width: '0.6875rem', height: '0.6875rem', color: '#ef4444' }} />
        <span style={{ fontWeight: 600, fontSize: '0.6875rem' }}>
          Trial Ended
        </span>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
          color: '#7c3aed',
          fontWeight: 600,
        }}>
          <Zap style={{ width: '0.625rem', height: '0.625rem' }} />
          Upgrade
        </span>
      </button>
    );
  }

  // Trial with <= 7 days remaining: warning style
  if (trialDaysRemaining != null && trialDaysRemaining <= 7) {
    return (
      <button
        onClick={onUpgradeClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.25rem 0.625rem',
          borderRadius: '9999px',
          border: '1px solid #fde68a',
          backgroundColor: '#fffbeb',
          cursor: 'pointer',
          fontSize: '0.75rem',
          lineHeight: '1rem',
          fontFamily: 'inherit',
          color: '#92400e',
          transition: 'all 150ms ease',
        }}
        title={`${trialDaysRemaining} days left on your free trial`}
      >
        <Zap style={{ width: '0.6875rem', height: '0.6875rem', color: '#f59e0b' }} />
        <span style={{ fontWeight: 600, fontSize: '0.6875rem' }}>
          {trialDaysRemaining}d left
        </span>
        <span style={{ color: 'var(--pc-border, #d1d5db)' }}>·</span>
        {sources && (
          <span style={{ color: getCounterColor(sources.usage, sources.limit), whiteSpace: 'nowrap' }}>
            {sources.usage}/{isUnlimited(sources.limit) ? '\u221E' : sources.limit}
            <span style={{ opacity: 0.6, marginLeft: '2px' }}>src</span>
          </span>
        )}
        {sources && posts && (
          <span style={{ color: 'var(--pc-border, #d1d5db)' }}>·</span>
        )}
        {posts && (
          <span style={{ color: getCounterColor(posts.usage, posts.limit), whiteSpace: 'nowrap' }}>
            {posts.usage}/{isUnlimited(posts.limit) ? '\u221E' : posts.limit}
            <span style={{ opacity: 0.6, marginLeft: '2px' }}>posts</span>
          </span>
        )}
      </button>
    );
  }

  // Trial with > 7 days remaining: subtle text
  if (trialDaysRemaining != null && trialDaysRemaining > 7) {
    const showUpgrade = isFreePlan || isOverLimit;
    return (
      <button
        onClick={onUpgradeClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.25rem 0.625rem',
          borderRadius: '9999px',
          border: '1px solid',
          borderColor: isOverLimit ? '#fecaca' : isNearLimit ? '#fde68a' : 'var(--pc-border)',
          backgroundColor: isOverLimit ? '#fef2f2' : isNearLimit ? '#fffbeb' : 'var(--pc-bg-subtle, #f9fafb)',
          cursor: 'pointer',
          fontSize: '0.75rem',
          lineHeight: '1rem',
          fontFamily: 'inherit',
          color: 'var(--pc-fg-muted, #6b7280)',
          transition: 'all 150ms ease',
        }}
        title={`Trial - ${trialDaysRemaining} days remaining`}
      >
        <span style={{
          fontWeight: 600,
          color: 'var(--pc-fg-base, #111827)',
          fontSize: '0.6875rem',
          letterSpacing: '0.025em',
        }}>
          Trial · {trialDaysRemaining}d left
        </span>
        <span style={{ color: 'var(--pc-border, #d1d5db)' }}>·</span>
        {sources && (
          <span style={{ color: getCounterColor(sources.usage, sources.limit), whiteSpace: 'nowrap' }}>
            {sources.usage}/{isUnlimited(sources.limit) ? '\u221E' : sources.limit}
            <span style={{ opacity: 0.6, marginLeft: '2px' }}>src</span>
          </span>
        )}
        {sources && posts && (
          <span style={{ color: 'var(--pc-border, #d1d5db)' }}>·</span>
        )}
        {posts && (
          <span style={{ color: getCounterColor(posts.usage, posts.limit), whiteSpace: 'nowrap' }}>
            {posts.usage}/{isUnlimited(posts.limit) ? '\u221E' : posts.limit}
            <span style={{ opacity: 0.6, marginLeft: '2px' }}>posts</span>
          </span>
        )}
        {showUpgrade && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            color: '#7c3aed',
            fontWeight: 600,
            marginLeft: '2px',
          }}>
            <Zap style={{ width: '0.625rem', height: '0.625rem' }} />
            Upgrade
          </span>
        )}
      </button>
    );
  }

  // Default: existing behavior (no trial)
  const showUpgrade = isFreePlan || isOverLimit;

  return (
    <button
      onClick={onUpgradeClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.25rem 0.625rem',
        borderRadius: '9999px',
        border: '1px solid',
        borderColor: isOverLimit ? '#fecaca' : isNearLimit ? '#fde68a' : 'var(--pc-border)',
        backgroundColor: isOverLimit ? '#fef2f2' : isNearLimit ? '#fffbeb' : 'var(--pc-bg-subtle, #f9fafb)',
        cursor: 'pointer',
        fontSize: '0.75rem',
        lineHeight: '1rem',
        fontFamily: 'inherit',
        color: 'var(--pc-fg-muted, #6b7280)',
        transition: 'all 150ms ease',
      }}
      title="View billing & usage"
    >
      {/* Plan name */}
      <span style={{
        fontWeight: 600,
        color: 'var(--pc-fg-base, #111827)',
        fontSize: '0.6875rem',
        textTransform: 'uppercase',
        letterSpacing: '0.025em',
      }}>
        {planName}
      </span>

      {/* Separator */}
      <span style={{ color: 'var(--pc-border, #d1d5db)' }}>·</span>

      {/* Sources counter */}
      {sources && (
        <span style={{ color: getCounterColor(sources.usage, sources.limit), whiteSpace: 'nowrap' }}>
          {sources.usage}/{isUnlimited(sources.limit) ? '\u221E' : sources.limit}
          <span style={{ opacity: 0.6, marginLeft: '2px' }}>src</span>
        </span>
      )}

      {/* Separator */}
      {sources && posts && (
        <span style={{ color: 'var(--pc-border, #d1d5db)' }}>·</span>
      )}

      {/* Posts counter */}
      {posts && (
        <span style={{ color: getCounterColor(posts.usage, posts.limit), whiteSpace: 'nowrap' }}>
          {posts.usage}/{isUnlimited(posts.limit) ? '\u221E' : posts.limit}
          <span style={{ opacity: 0.6, marginLeft: '2px' }}>posts</span>
        </span>
      )}

      {/* Upgrade CTA */}
      {showUpgrade && (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
          color: '#7c3aed',
          fontWeight: 600,
          marginLeft: '2px',
        }}>
          <Zap style={{ width: '0.625rem', height: '0.625rem' }} />
          Upgrade
        </span>
      )}
    </button>
  );
}
